import { Performance as PerformanceIcon, ViewSidebar as ViewSidebarIcon } from '@tunasong/icons'
import type { BaseLayoutSpec } from '@tunasong/plugin-lib'
import { useLayout, useLayoutsExtended } from '@tunasong/plugin-lib'
import {
  FavoriteIcon,
  IconButton,
  LayoutSelector,
  Presence,
  TunaEntityBreadcrumbs,
  useDimensions,
  useLayoutNavigation,
  useSidebar,
  ToggleButton,
} from '@tunasong/ui-lib'
import type { MouseEvent, ReactNode } from 'react'
import CreateEntityButton from '../entity/create-entity-button.js'
import { Header } from '../header.js'
import type { ChromeProps } from './chrome.js'
import { EntityMenuButton } from './entity-menu.js'
import styles from './entity-header.module.css'

export interface EntityHeaderProps extends ChromeProps {
  ref?: React.Ref<HTMLDivElement>
  /** Extra controls appended to the default header toolbar. */
  additionalControls?: ReactNode
}

const performanceLayoutName = 'Performance'

export const EntityHeader = (props: EntityHeaderProps) => {
  const { entity, layoutName, showSidebar, ref, additionalControls } = props

  const { isTiny } = useDimensions()

  const layouts = useLayoutsExtended(entity)
  const { setLayout } = useLayoutNavigation({ entity })

  const navigateToLayout = (layout: BaseLayoutSpec) => setLayout(layout.name)

  const layout = useLayout({ entity, layoutName })

  const entityId = entity?.id

  const { setActiveTool, open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()

  const handleToggleSidebar = (ev: MouseEvent) => {
    ev.preventDefault()
    if (!sidebarOpen) {
      setSidebarOpen(true)
      setActiveTool('Settings')
    } else {
      setSidebarOpen(false)
      setActiveTool(null)
    }
  }

  const container = layout?.container ? layout.container : {}

  /** The dedicated Performance icon replaces the Performance entry in the layout selector */
  const showPerformanceIcon = Boolean(
    entity && layouts.some(l => l.name === performanceLayoutName) && layout?.name !== performanceLayoutName
  )
  const selectorLayouts = showPerformanceIcon ? layouts.filter(l => l.name !== performanceLayoutName) : layouts
  /** Without Performance, a lone remaining layout would leave a disabled one-item selector (e.g. Folder, Setlist) */
  const showLayoutSelector = showPerformanceIcon ? selectorLayouts.length > 1 : selectorLayouts.length > 0

  return (
    <Header
      ref={ref}
      position="absolute"
      elevation={0}
      controls={[
        additionalControls ? <span key={-2}>{additionalControls}</span> : null,
        entity ? <FavoriteIcon entity={entity} key={-1} /> : null,
        entity ? <CreateEntityButton key={0} parent={entity} /> : null,
        showLayoutSelector ? (
          <LayoutSelector
            // variant="outlined"
            selected={layout ?? undefined}
            layouts={selectorLayouts}
            key={211}
            onChange={navigateToLayout}
          />
        ) : null,
        showPerformanceIcon ? (
          <IconButton
            key={212}
            aria-label="Enter Performance mode"
            title="Perform"
            onClick={() => setLayout(performanceLayoutName)}
          >
            <PerformanceIcon />
          </IconButton>
        ) : null,

        <Presence key={10} entityId={entityId} className={styles.presence} />,
        showSidebar && isTiny ? (
          <ToggleButton
            size="small"
            value="sidebar"
            selected={sidebarOpen}
            key={11}
            onClick={handleToggleSidebar}
            aria-label="Toggle sidebar"
          >
            <ViewSidebarIcon color={sidebarOpen ? 'secondary' : undefined} />
          </ToggleButton>
        ) : null,
        <EntityMenuButton key={12} entity={entity} className={styles.menuButton} />,
      ].filter(Boolean)}
      showBackButton={container?.showBreadcrumbs !== false}
      breadcrumbs={
        container?.showBreadcrumbs !== false ? (
          <TunaEntityBreadcrumbs
            className={styles.breadcrumbs}
            entity={entity}
            showHome={true}
            mode={isTiny ? 'menu' : 'inline'}
          />
        ) : undefined
      }
    />
  )
}
